import { useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
import {
  getGetCustomReportQueryKey,
  getGetReportSectionQueryKey,
  getListCustomReportsByCaseQueryKey,
  useGetReportSection,
  useRegenerateSection,
  useUpdateReportSection,
} from "~/api/custom-reports/custom-reports";
import type { Report } from "~/api/model";
import { generateIdempotencyKey } from "./reportUtils";

export const useSectionData = (
  organizationId: string,
  projectId: string,
  reportId: string,
  sectionId: string,
  sectionStatus: string | undefined,
  isExpanded: boolean,
  isVisible: boolean
) => {
  const queryClient = useQueryClient();

  const { data: sectionData, isLoading } = useGetReportSection(
    organizationId,
    projectId,
    reportId,
    sectionId,
    {
      query: {
        enabled:
          isExpanded &&
          isVisible &&
          !!sectionId &&
          !!reportId &&
          sectionStatus === "completed",
      },
    }
  );

  const { mutate: regenerateSection } = useRegenerateSection({
    mutation: {
      onMutate: async (variables) => {
        await queryClient.cancelQueries({
          queryKey: getGetCustomReportQueryKey(
            organizationId,
            projectId,
            reportId
          ),
        });

        const previousReport = queryClient.getQueryData(
          getGetCustomReportQueryKey(organizationId, projectId, reportId)
        );

        queryClient.setQueryData(
          getGetCustomReportQueryKey(organizationId, projectId, reportId),
          (old: Report | undefined) => {
            if (!old?.reportSections) return old;

            return {
              ...old,
              reportSections: old.reportSections.map((section) =>
                section.id === variables.reportSectionId
                  ? { ...section, status: "pending" as const }
                  : section
              ),
            };
          }
        );

        return { previousReport };
      },
      onError: (_err, _variables, context) => {
        if (context?.previousReport) {
          queryClient.setQueryData(
            getGetCustomReportQueryKey(organizationId, projectId, reportId),
            context.previousReport
          );
        }
      },
      onSuccess: async (_data, variables) => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: getGetCustomReportQueryKey(
              organizationId,
              projectId,
              reportId
            ),
          }),
          queryClient.invalidateQueries({
            queryKey: getGetReportSectionQueryKey(
              organizationId,
              projectId,
              variables.reportId,
              variables.reportSectionId
            ),
          }),
          queryClient.invalidateQueries({
            queryKey: getListCustomReportsByCaseQueryKey(
              organizationId,
              projectId
            ),
          }),
        ]);
      },
    },
  });

  const { mutate: updateReportSection } = useUpdateReportSection({
    mutation: {
      onSuccess: async (_data, variables) => {
        await queryClient.invalidateQueries({
          queryKey: getGetReportSectionQueryKey(
            organizationId,
            projectId,
            variables.reportId,
            variables.reportSectionId
          ),
        });
      },
    },
  });

  const handleRegenerate = useCallback(() => {
    if (!sectionId) return;
    regenerateSection({
      organizationId,
      projectId,
      reportId,
      reportSectionId: sectionId,
      data: {
        idempotencyKey: generateIdempotencyKey(),
      },
    });
  }, [regenerateSection, organizationId, projectId, reportId, sectionId]);

  const handleEdit = useCallback(
    async (content: string): Promise<void> => {
      if (!sectionId) return;
      return new Promise((resolve, reject) => {
        updateReportSection(
          {
            organizationId,
            projectId,
            reportId,
            reportSectionId: sectionId,
            data: { content },
          },
          {
            onSuccess: () => resolve(),
            onError: (error) => reject(error),
          }
        );
      });
    },
    [updateReportSection, organizationId, projectId, reportId, sectionId]
  );

  return {
    sectionData,
    isLoading,
    handleRegenerate,
    handleEdit,
  };
};
